// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// logAnalytics (#247) — pure helpers behind the Log viewer's analytics charts.
//
// Time-buckets a list of log events and counts them by level (a STATUS
// dimension: error / warn / info-or-event), and tallies the top events. Pure and
// deterministic (no Date.now(); the window is passed in), so the charts and their
// tests share one implementation.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// Normalize any source's level string to one of three status buckets.
export function normalizeLevel(level) {
  const l = String(level || "").toLowerCase();
  if (l === "error" || l === "fatal" || l === "critical") return "error";
  if (l === "warn" || l === "warning") return "warn";
  return "info"; // INFO / DEBUG / TRACE + the platform START/END/REPORT lines
}

// Status colors (reserved; always shipped with a label + legend — never reused
// as a categorical series color).
export const LEVEL_STATUS = {
  error: { label: "Error", color: "#dc2626" },
  warn: { label: "Warn", color: "#d97706" },
  info: { label: "Info / event", color: "#2563eb" },
};
export const LEVEL_ORDER = ["error", "warn", "info"];

// bucketByLevel — split [startMs, endMs] into `buckets` equal windows and count
// events per bucket per normalized level. `timeOf`/`levelOf` read the fields off
// whatever row shape the source produces.
//   → [{ bucketStart, label, error, warn, info, total }]
export function bucketByLevel(events, { startMs, endMs, buckets = 24, timeOf, levelOf }) {
  const getTime = timeOf || ((e) => e.timestamp ?? e.receivedAt);
  const getLevel = levelOf || ((e) => e.level);
  const span = Math.max(1, endMs - startMs);
  const width = span / buckets;

  const out = [];
  for (let i = 0; i < buckets; i++) {
    const bucketStart = startMs + i * width;
    out.push({
      bucketStart,
      label: new Date(bucketStart).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      error: 0,
      warn: 0,
      info: 0,
      total: 0,
    });
  }

  for (const e of events || []) {
    const t = Number(getTime(e));
    if (!Number.isFinite(t) || t < startMs || t > endMs) continue;
    let idx = Math.floor((t - startMs) / width);
    if (idx < 0) idx = 0;
    if (idx >= buckets) idx = buckets - 1;
    const lvl = normalizeLevel(getLevel(e));
    out[idx][lvl] += 1;
    out[idx].total += 1;
  }
  return out;
}

// topBy — tally the top-N values of a field (e.g. the CloudWatch message
// text, or a request id), descending by count.
//   → [{ key, count }]
export function topBy(events, keyOf, topN = 10) {
  const counts = new Map();
  for (const e of events || []) {
    const k = keyOf(e);
    if (k === undefined || k === null || k === "") continue;
    counts.set(k, (counts.get(k) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, topN);
}

// levelTotals — overall count per status level across the whole set.
export function levelTotals(events, levelOf) {
  const getLevel = levelOf || ((e) => e.level);
  const totals = { error: 0, warn: 0, info: 0 };
  for (const e of events || []) totals[normalizeLevel(getLevel(e))] += 1;
  return totals;
}
